/**
 * Manual creation of a Commission Invoice / Credit Note / Debit Note.
 * Shared by both Admin -> Vendor Commissions and Admin -> Commission
 * Invoices pages. Every Credit/Debit Note must be linked to an existing
 * Order, Invoice, or Credit/Debit Note — no free-standing adjustments,
 * per admin policy (keeps every manual document traceable/auditable).
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Loader2, Search, Check, Receipt, FileMinus, FilePlus } from 'lucide-react';

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const DOC_TYPES = [
  { id: 'credit_note', label: 'Credit Note', icon: FileMinus, color: 'red' },
  { id: 'debit_note', label: 'Debit Note', icon: FilePlus, color: 'emerald' },
  { id: 'invoice', label: 'Invoice', icon: Receipt, color: 'blue' },
];
const LINK_TYPES = [
  { id: 'order', label: 'Order' },
  { id: 'invoice', label: 'Existing Invoice' },
  { id: 'credit_note', label: 'Existing Credit Note' },
  { id: 'debit_note', label: 'Existing Debit Note' },
];

function PillGroup({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            value === o.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
          }`}
          data-testid={`${testidPrefix}-${o.id}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function ManualCommissionDocumentModal({ open, onOpenChange, onCreated }) {
  const { apiCall } = useAuth();

  const [docType, setDocType] = useState('credit_note');
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');

  const [linkType, setLinkType] = useState('order');
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(''); // for link_type=invoice w/ multiple orders

  const [orderIds, setOrderIds] = useState([]); // manual invoice — selected orders
  const [month, setMonth] = useState('');

  const [commissionAmount, setCommissionAmount] = useState('');
  const [commissionGst, setCommissionGst] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiCall('/api/admin/vendor-billing/vendors?limit=300').then(d => setVendors(d.vendors || [])).catch(() => {});
  }, [open]);

  function reset() {
    setDocType('credit_note'); setVendorQuery(''); setVendorId('');
    setLinkType('order'); setLinkQuery(''); setLinkResults([]); setSelectedLink(null); setSelectedOrderId('');
    setOrderIds([]); setMonth(''); setCommissionAmount(''); setCommissionGst(''); setReason('');
  }
  function close() {
    reset();
    onOpenChange(false);
  }

  const filteredVendors = vendors.filter(v => {
    const q = vendorQuery.toLowerCase();
    return !q || (v.business_name || '').toLowerCase().includes(q) || (v.email || '').toLowerCase().includes(q);
  });

  const searchLinks = useCallback(async () => {
    if (!vendorId) return;
    setLinkLoading(true);
    setLinkResults([]);
    try {
      if (linkType === 'order') {
        const params = new URLSearchParams({ vendor_id: vendorId, limit: '30' });
        if (linkQuery) params.set('q', linkQuery);
        const data = await apiCall(`/api/admin/commission-invoices/manual/vendor-orders?${params}`);
        setLinkResults(Array.isArray(data) ? data : []);
      } else if (linkType === 'invoice') {
        const data = await apiCall(`/api/admin/commission-invoices/?vendor_id=${vendorId}&limit=50`);
        const list = Array.isArray(data) ? data : [];
        setLinkResults(linkQuery ? list.filter(i => (i.invoice_number || '').toLowerCase().includes(linkQuery.toLowerCase())) : list);
      } else {
        const data = await apiCall(`/api/admin/commission-invoices/credit-notes?vendor_id=${vendorId}`);
        const list = (Array.isArray(data) ? data : []).filter(n => n.document_type === linkType);
        setLinkResults(linkQuery ? list.filter(n => (n.credit_note_number || '').toLowerCase().includes(linkQuery.toLowerCase())) : list);
      }
    } catch {
      toast.error('Failed to search');
    } finally {
      setLinkLoading(false);
    }
  }, [vendorId, linkType, linkQuery]);

  useEffect(() => {
    setSelectedLink(null);
    setLinkResults([]);
    if (vendorId && docType !== 'invoice') searchLinks();
  }, [vendorId, linkType, docType]);

  useEffect(() => {
    setOrderIds([]);
    setLinkResults([]);
    if (vendorId && docType === 'invoice') searchLinks();
  }, [vendorId, docType]);

  function toggleOrderSelected(orderId) {
    setOrderIds(prev => prev.includes(orderId) ? prev.filter(x => x !== orderId) : [...prev, orderId]);
  }

  async function handleSubmit() {
    if (!vendorId) { toast.error('Select a vendor'); return; }
    setSubmitting(true);
    try {
      if (docType === 'invoice') {
        if (orderIds.length === 0) { toast.error('Select at least one order'); setSubmitting(false); return; }
        const inv = await apiCall('/api/admin/commission-invoices/manual/invoice', {
          method: 'POST',
          body: JSON.stringify({ vendor_id: vendorId, order_ids: orderIds, month: month || null, create_payment_link: false }),
        });
        toast.success(`Invoice ${inv.invoice_number} created — ${fmt(inv.grand_total)}`);
      } else {
        if (!selectedLink) { toast.error('Select a link (order / invoice / note) first'); setSubmitting(false); return; }
        if (!commissionAmount || !reason.trim()) { toast.error('Commission amount and reason are required'); setSubmitting(false); return; }
        const linkId = linkType === 'order' ? selectedLink.order_id : selectedLink.id;
        const note = await apiCall('/api/admin/commission-invoices/manual/note', {
          method: 'POST',
          body: JSON.stringify({
            document_type: docType,
            vendor_id: vendorId,
            link_type: linkType,
            link_id: linkId,
            order_id: linkType === 'invoice' ? selectedOrderId || null : null,
            commission_amount: Number(commissionAmount),
            commission_gst: Number(commissionGst || 0),
            reason: reason.trim(),
          }),
        });
        toast.success(`${docType === 'debit_note' ? 'Debit' : 'Credit'} Note ${note.credit_note_number} created`);
      }
      onCreated && onCreated();
      close();
    } catch (e) {
      const msg = e?.detail?.detail || e?.detail || e?.message || 'Failed to create document';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  }

  const invoiceHasMultipleOrders = linkType === 'invoice' && selectedLink && (selectedLink.line_items || []).length > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-document-modal">
        <DialogHeader>
          <DialogTitle>Create Document</DialogTitle>
          <DialogDescription>
            Manually create a commission Invoice, Credit Note, or Debit Note. Every Credit/Debit
            Note must be linked to an existing Order, Invoice, or Note — the system auto-numbers it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">Document Type</label>
            <PillGroup options={DOC_TYPES} value={docType} onChange={setDocType} testidPrefix="doc-type" />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">Vendor</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Search vendor by name or email…"
                value={vendorId ? (vendors.find(v => v.id === vendorId)?.business_name || vendorId) : vendorQuery}
                onChange={e => { setVendorId(''); setVendorQuery(e.target.value); }}
                className="h-9 text-sm pl-8"
                data-testid="manual-doc-vendor-search"
              />
            </div>
            {vendorQuery && !vendorId && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {filteredVendors.slice(0, 15).map(v => (
                  <button
                    key={v.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between"
                    onClick={() => { setVendorId(v.id); setVendorQuery(''); }}
                    data-testid={`manual-doc-vendor-option-${v.id}`}
                  >
                    <span>{v.business_name || v.email}</span>
                    <span className="text-xs text-gray-400">{v.plan_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {docType === 'invoice' ? (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">
                  Select Order(s) to Invoice {linkLoading && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
                </label>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {linkResults.length === 0 && !linkLoading && (
                    <p className="text-xs text-gray-400 text-center py-4">
                      {vendorId ? 'No settled orders found for this vendor' : 'Select a vendor first'}
                    </p>
                  )}
                  {linkResults.map(o => (
                    <label key={o.order_id} className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${o.already_invoiced ? 'opacity-50' : ''}`} data-testid={`manual-invoice-order-${o.order_id}`}>
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={orderIds.includes(o.order_id)}
                          disabled={o.already_invoiced}
                          onChange={() => toggleOrderSelected(o.order_id)}
                        />
                        #{o.order_number || o.order_id.slice(0, 8)}
                        {o.already_invoiced && <span className="text-xs text-amber-600">already on {o.invoice_number}</span>}
                      </span>
                      <span className="text-xs text-gray-500">{fmt(o.commission)} + {fmt(o.commission_gst)} GST</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">Month (optional override)</label>
                <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9 text-sm w-44" data-testid="manual-invoice-month" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">Link To</label>
                <PillGroup options={LINK_TYPES} value={linkType} onChange={setLinkType} testidPrefix="link-type" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">
                  Search {LINK_TYPES.find(l => l.id === linkType)?.label} {linkLoading && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
                </label>
                <Input
                  placeholder="Search by number / order id…"
                  value={linkQuery}
                  onChange={e => setLinkQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchLinks()}
                  className="h-9 text-sm mb-2"
                  data-testid="manual-doc-link-search"
                />
                <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                  {linkResults.length === 0 && !linkLoading && (
                    <p className="text-xs text-gray-400 text-center py-4">{vendorId ? 'No results' : 'Select a vendor first'}</p>
                  )}
                  {linkResults.map(item => {
                    const key = item.id || item.order_id;
                    const label = linkType === 'order'
                      ? `#${item.order_number || item.order_id.slice(0, 8)} · ${fmt(item.commission)}`
                      : linkType === 'invoice'
                        ? `${item.invoice_number} · ${fmt(item.grand_total)}`
                        : `${item.credit_note_number} · ${fmt(Math.abs(item.grand_total))}`;
                    const selected = selectedLink && (selectedLink.id || selectedLink.order_id) === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedLink(item)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${selected ? 'bg-gray-900 text-white' : ''}`}
                        data-testid={`manual-doc-link-option-${key}`}
                      >
                        {label}
                        {selected && <Check className="h-3.5 w-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {invoiceHasMultipleOrders && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 font-medium">Which order on this invoice?</label>
                  <select
                    className="w-full h-9 text-sm border border-gray-200 rounded-md px-2"
                    value={selectedOrderId}
                    onChange={e => setSelectedOrderId(e.target.value)}
                    data-testid="manual-doc-invoice-order-select"
                  >
                    <option value="">Select order…</option>
                    {selectedLink.line_items.map(li => (
                      <option key={li.order_id} value={li.order_id}>#{li.order_number || li.order_id.slice(0, 8)}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 font-medium">Commission Amount (₹)</label>
                  <Input type="number" value={commissionAmount} onChange={e => setCommissionAmount(e.target.value)} className="h-9 text-sm" data-testid="manual-doc-commission-amount" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 font-medium">GST (₹)</label>
                  <Input type="number" value={commissionGst} onChange={e => setCommissionGst(e.target.value)} className="h-9 text-sm" data-testid="manual-doc-commission-gst" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">Reason</label>
                <Textarea value={reason} onChange={e => setReason(e.target.value)} className="text-sm" rows={2} data-testid="manual-doc-reason" />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="manual-doc-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="manual-doc-submit-btn">
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
